import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { Bank, BankBasicDetails } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { tr } from 'date-fns/locale';

interface PaginatedBankResponse {
  data: Bank[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

interface BasicBank {
  id: string;
  bankName: string;
  accountNumber: string;
}

interface BasicBankResponse {
  data: BasicBank[];
}

export const useGetBank = () => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();
  const [bankBasics, setBankBasics] = useState<BankBasicDetails[]>([]);

  const fetchBanks = async (
    page = 1,
    limit = 100,
    search = '',
    sortBy = 'bankName',
    order = 'ASC'
  ) => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedBankResponse>(
        apiUrls.BANK_MANAGEMENT,
        {
          params: {
            offset: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            order: order,
          },
          withCredentials: true,
        }
      );

      setBanks(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setBanks([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const fetchBasicDetails = async () => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedBankResponse>(
        apiUrls.GET_BANK_ACCOUNTS_BASIC_DETAILS,
        {
          withCredentials: true,
        }
      );

      setBankBasics(response.data.data || []);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setBanks([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteBank = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(apiUrls.DELETE_BANK, {
        params: { id },
      });
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  return {
    loading,
    banks,
    totalItems,
    totalPages,
    currentPage,
    bankBasics,
    fetchBanks,
    deleteBank,
    fetchBasicDetails,
  };
};

export const useGetBasicBanks = () => {
  const [basicBanks, setBasicBanks] = useState<BasicBank[]>([]);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = StyledSnackbar();

  const fetchBasicBanks = async () => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<BasicBankResponse>(
        apiUrls.BANK_LIST_BASIC,
        {
          withCredentials: true,
        }
      );

      setBasicBanks(response.data.data || []);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setBasicBanks([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    basicBanks,
    fetchBasicBanks,
  };
};
